/**
 * Performance2DCanvas — 2D performance mode canvas.
 *
 * Renders exposed polyline outputs from the current definition.
 * Delegates rendering to Polyline2DRenderer (via the video registry).
 * This component owns the canvas element and RAF loop.
 *
 * Pointer interaction is delegated to the Interaction2D interface
 * resolved from the polylines source node.
 */

import { useRef, useEffect } from 'react';
import { useScope } from '../../contexts/ScopeContext';
import { useRafLoop } from '@/ui/hooks/useRafLoop';
import { webRuntimeApi } from '@/runtime/web';
import type { Interaction2D } from '@/nodes/Interaction2D';
import { TweakablePanel } from './TweakablePanel';

export function Performance2DCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { instanceId, definitionId, definitionState } = useScope();
  const interactionRef = useRef<Interaction2D | null>(null);

  // Keep latest values in refs so native listeners can read them
  const instanceIdRef = useRef(instanceId);
  instanceIdRef.current = instanceId;
  const definitionStateRef = useRef(definitionState);
  definitionStateRef.current = definitionState;

  // ─── Activate / deactivate renderer ───
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    webRuntimeApi.activatePerformanceView2D(instanceId, canvas);

    // Initial size
    const container = containerRef.current;
    if (container) {
      const renderer = webRuntimeApi.getPerformance2DRenderer(instanceId);
      if (renderer) {
        const { clientWidth: w, clientHeight: h } = container;
        renderer.setSize(w, h);
      }
    }

    // Resize observer
    const ro = new ResizeObserver((entries) => {
      const renderer = webRuntimeApi.getPerformance2DRenderer(instanceId);
      if (!renderer) return;
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          renderer.setSize(width, height);
        }
      }
    });
    if (container) ro.observe(container);

    return () => {
      ro.disconnect();
      webRuntimeApi.deactivatePerformanceView2D(instanceId);
    };
  }, [instanceId]);

  // ─── RAF loop: collect polylines + render ───
  useRafLoop(() => {
    if (!definitionState) return;

    const renderer = webRuntimeApi.getPerformance2DRenderer(instanceId);
    if (!renderer) return;

    renderer.collectPolylines(definitionState.api);
    renderer.render();
  }, [definitionState, instanceId]);

  // ─── Native pointer events for drag ───
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onPointerDown = (e: PointerEvent) => {
      const iid = instanceIdRef.current;
      const defState = definitionStateRef.current;

      const renderer = webRuntimeApi.getPerformance2DRenderer(iid);
      if (!renderer || !defState?.api) return;

      const rect = canvas.getBoundingClientRect();
      const cssX = e.clientX - rect.left;
      const cssY = e.clientY - rect.top;
      const data = renderer.canvasToData(cssX, cssY);
      const dpr = window.devicePixelRatio || 1;
      const radius = 10 * dpr / renderer.getScale();

      // Try topmost layer first (reverse of draw order)
      const interactions = renderer.resolveAllInteractions2D(defState.api);
      for (let i = interactions.length - 1; i >= 0; i--) {
        if (interactions[i].onPointerDown(data.x, data.y, radius)) {
          interactionRef.current = interactions[i];
          canvas.setPointerCapture(e.pointerId);
          return;
        }
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction) return;

      const iid = instanceIdRef.current;
      const renderer = webRuntimeApi.getPerformance2DRenderer(iid);
      if (!renderer) return;

      const rect = canvas.getBoundingClientRect();
      const cssX = e.clientX - rect.left;
      const cssY = e.clientY - rect.top;
      const data = renderer.canvasToData(cssX, cssY);

      interaction.onPointerMove(data.x, data.y);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!interactionRef.current) return;
      interactionRef.current.onPointerUp();
      interactionRef.current = null;
      canvas.releasePointerCapture(e.pointerId);
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          touchAction: 'none',
        }}
      />
      <TweakablePanel overlay scopeDefinitionId={definitionId} />
    </div>
  );
}
